import { createRouter, createWebHistory } from "vue-router";
import AppHome from "@/pages/Home.vue";
import MaintenancePage from "@/pages/MaintenancePage.vue";
import { hasAdminPermission, logout } from '@/services/auth';

const ProductDetails = () => import(/* webpackChunkName: "product-details" */ "@/pages/ProductDetails.vue");
const ProductList = () => import(/* webpackChunkName: "product-list" */ "@/pages/ProductList.vue");
const BankList = () => import(/* webpackChunkName: "bank-list" */ "@/pages/BankList.vue");
const ShoppingCart = () => import(/* webpackChunkName: "cart" */ "@/pages/ShoppingCart.vue");
const ProductForm = () => import(/* webpackChunkName: "admin-product-form" */ "@/components/admin/ProductForm.vue");
const LoginForm = () => import(/* webpackChunkName: "auth" */ "@/pages/LoginForm.vue");
const AdminProductList = () => import(/* webpackChunkName: "admin-products" */ "@/pages/admin/AdminProductList.vue");
const AdminOrderList = () => import(/* webpackChunkName: "admin-orders" */ "@/pages/admin/AdminOrderList.vue");
const AdminOrderDetails = () => import(/* webpackChunkName: "admin-order-detail" */ "@/pages/admin/AdminOrderDetails.vue");
const AdminNewSale = () => import(/* webpackChunkName: "admin-new-sale" */ "@/pages/admin/AdminNewSale.vue");
const HowToGerminate = () => import(/* webpackChunkName: "germinate" */ "@/pages/HowToGerminate.vue");
const AccountSettings = () => import(/* webpackChunkName: "account" */ "@/pages/AccountSettings.vue");
const RegisterForm = () => import(/* webpackChunkName: "auth" */ "@/pages/RegisterForm.vue");
const ActivityPage = () => import(/* webpackChunkName: "admin-activity" */ "@/pages/ActivityPage.vue");
const BankForm = () => import(/* webpackChunkName: "admin-bank" */ "@/pages/admin/BankForm.vue");
const AdminBankList = () => import(/* webpackChunkName: "admin-banks" */ "@/pages/admin/AdminBankList.vue");
const AdminWarrantyClaims = () => import(/* webpackChunkName: "admin-warranty" */ "@/pages/admin/AdminWarrantyClaims.vue");
const BrandList = () => import(/* webpackChunkName: "admin-brands" */ '@/pages/admin/BrandList.vue');
const UserList = () => import(/* webpackChunkName: "admin-users" */ '@/pages/admin/UserList.vue');
const UserDetail = () => import(/* webpackChunkName: "admin-user-detail" */ '@/pages/admin/UserDetail.vue');
const CarouselManager = () => import(/* webpackChunkName: "admin-carousel" */ "@/components/admin/CarouselManager.vue");

const routes = [
  {
    path: "/maintenance",
    name: "MaintenancePage",
    component: MaintenancePage,
  },
  {
    path: "/",
    name: "Home",
    component: AppHome,
  },
  {
    path: "/mi-cuenta",
    name: "AccountSettings",
    component: AccountSettings,
    meta: { requiresAuth: true }
  },
  {
    path: "/como-germinar/",
    name: "HowToGerminate",
    component: HowToGerminate,
  },
  {
    path: "/producto/:id",
    name: "ProductDetails",
    component: ProductDetails,
  },
  {
    path: "/productos/",
    name: "ProductList",
    component: ProductList,
  },
  {
    path: "/bancos/",
    name: "BankList",
    component: BankList,
  },
  {
    path: "/carrito/",
    name: "ShoppingCart",
    component: ShoppingCart,
  },
  {
    path: "/admin",
    name: "AdminArea",
    meta: { requiresAuth: true, requiresAdmin: true },
    children: [
      {
        path: "carousel",
        name: "CarouselManager",
        component: CarouselManager,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "productos",
        name: "AdminProductList",
        component: AdminProductList
      },
      {
        path: "pedidos",
        name: "AdminOrderList",
        component: AdminOrderList
      },
      {
        path: "ventas/nueva",
        name: "AdminNewSale",
        component: AdminNewSale
      },
      {
        path: "pedidos/:orderId",
        name: "AdminOrderDetails",
        component: AdminOrderDetails
      },
      {
        path: "productos/nuevo",
        name: "ProductForm",
        component: ProductForm
      },
      {
        path: "productos/editar/:productId",
        name: "ProductEdit",
        component: ProductForm
      },
      {
        path: "actividad",
        name: "ActivityLog",
        component: ActivityPage,
        meta: { 
          requiresAuth: true,
          requiresAdmin: true 
        }
      },
      {
        path: "bancos/nuevo",
        name: "BankForm",
        component: BankForm
      },
      {
        path: "bancos/editar/:id",
        name: "BankEdit",
        component: BankForm
      },
      {
        path: "bancos",
        name: "AdminBankList",
        component: AdminBankList
      },
      {
        path: "garantias",
        name: "AdminWarrantyClaims",
        component: AdminWarrantyClaims
      },
      {
        path: "marcas",
        name: "BrandList",
        component: BrandList,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "usuarios",
        name: "UserList",
        component: UserList,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "usuarios/:id",
        name: "UserDetail",
        component: UserDetail,
        meta: { requiresAuth: true, requiresAdmin: true }
      },
      {
        path: "configuracion",
        name: "SiteSettings",
        component: () => import('@/pages/admin/SiteSettings.vue'),
        meta: { requiresAuth: true, requiresAdmin: true }
      }
    ]
  },
  {
    path: "/login",
    name: "LoginForm",
    component: LoginForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/crear-cuenta",
    name: "RegisterForm",
    component: RegisterForm,
    meta: { redirectIfAuth: true }
  },
  {
    path: "/recuperar-contrasena",
    name: "PasswordReset",
    component: () => import('@/pages/PasswordReset.vue'),
    meta: { redirectIfAuth: true }
  },
  {
    path: "/:pathMatch(.*)*",
    redirect: "/"
  }
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior() {
    return { top: 0 }
  },
});

router.beforeEach(async (to, from, next) => {
  const isAuthenticated = !!localStorage.getItem('accessToken');
  
  // Si intenta acceder a login, permitir siempre
  if (to.name === 'LoginForm') {
    return next();
  }
  
  // Si intenta acceder a la página de mantenimiento, verificar si realmente está en mantenimiento
  if (to.name === 'MaintenancePage') {
    try {
      // Importar dinámicamente el servicio de mantenimiento
      const maintenanceServiceModule = await import('@/services/maintenanceService');
      const maintenanceService = maintenanceServiceModule.default;
      
      // Verificar si el sitio está realmente en mantenimiento
      const isInMaintenance = await maintenanceService.isInMaintenance(true);
      
      if (!isInMaintenance) {
        // Si no está en mantenimiento, redirigir a la página principal
        console.log('Sitio no está en mantenimiento: Redirigiendo a inicio');
        return next('/');
      }
      
      // Si está en mantenimiento, permitir acceso a la página
      return next();
    } catch (error) {
      console.error('Error verificando estado de mantenimiento para acceso a /maintenance:', error);
      // En caso de error, redirigir a inicio por seguridad
      return next('/');
    }
  }
  
  // Verificar si el sitio está en mantenimiento
  let maintenanceService;
  let isInMaintenance = false;
  
  try {
    // Importar dinámicamente el servicio de mantenimiento
    const maintenanceServiceModule = await import('@/services/maintenanceService');
    maintenanceService = maintenanceServiceModule.default;
    
    // Verificar si hay un estado en caché para evitar bloquear la navegación
    const now = Date.now();
    const cacheKey = 'maintenance_cache';
    const cacheTimestampKey = 'maintenance_cache_timestamp';
    const cacheTTL = 300000; // 5 minutos
    
    // Verificar si tenemos un caché válido
    const cachedState = localStorage.getItem(cacheKey);
    const cachedTimestamp = parseInt(localStorage.getItem(cacheTimestampKey) || '0', 10);
    const cacheValid = cachedTimestamp && (now - cachedTimestamp < cacheTTL);
    
    if (cacheValid && cachedState !== null) {
      // Usar el caché para evitar bloquear la navegación
      isInMaintenance = cachedState === 'true';
      
      // Actualizar en segundo plano si es necesario
      maintenanceService.isInMaintenance(false).then(state => {
        localStorage.setItem(cacheKey, state.toString());
        localStorage.setItem(cacheTimestampKey, Date.now().toString());
      }).catch(err => console.error('Error en actualización en segundo plano:', err));
    } else {
      // Si no hay caché válido, debemos esperar la verificación
      isInMaintenance = await maintenanceService.isInMaintenance(false);
      localStorage.setItem(cacheKey, isInMaintenance.toString());
      localStorage.setItem(cacheTimestampKey, Date.now().toString());
    }
  } catch (error) {
    console.error('Error verificando estado de mantenimiento:', error);
    // En caso de error, permitir continuar para no bloquear el acceso
    isInMaintenance = false;
  }
  
  // Si estamos en mantenimiento, verificar permisos de acceso
  if (isInMaintenance && maintenanceService) {
    try {
      // Guardar la ruta intentada para redirigir después de autenticar/bypass
      if (to.name !== 'MaintenancePage') {
        localStorage.setItem('intendedRoute', to.fullPath);
      }
      
      // Primero, verificar si el usuario ha validado la contraseña de bypass
      const bypassValidated = maintenanceService.hasValidatedBypass();
      if (bypassValidated) {
        console.log('Sitio en mantenimiento: Usuario con bypass por contraseña');
        // Usuario con contraseña validada, permitir acceso
        return next();
      }
      
      // Si está autenticado, verificar si es administrador para permitir bypass
      if (isAuthenticated) {
        try {
          const isAdmin = await hasAdminPermission();
          if (!isAdmin) {
            // Usuario autenticado pero no es admin, redirigir a mantenimiento
            console.log('Sitio en mantenimiento: Usuario no es administrador');
            return next({ name: 'MaintenancePage' });
          }
          // Es administrador, permitir acceso
          console.log('Sitio en mantenimiento: Acceso permitido para administrador');
        } catch (error) {
          console.error('Error verificando permisos de admin en modo mantenimiento:', error);
          // En caso de error, redirigir a mantenimiento
          return next({ name: 'MaintenancePage' });
        }
      } else {
        // No está autenticado, redirigir a mantenimiento
        console.log('Sitio en mantenimiento: Usuario no autenticado');
        return next({ name: 'MaintenancePage' });
      }
    } catch (error) {
      console.error('Error procesando lógica de mantenimiento:', error);
      // Si hay un error en la lógica, redirigir a la página de mantenimiento por seguridad
      return next({ name: 'MaintenancePage' });
    }
  }  // Redirigir si ya está autenticado e intenta acceder a login
  if (to.meta.redirectIfAuth && isAuthenticated) {
    return next('/mi-cuenta');
  }

  // Verificar si la ruta requiere autenticación
  if (to.meta.requiresAuth) {
    if (!isAuthenticated) {
      // Guardar la ruta intentada para redirigir después del login
      localStorage.setItem('intendedRoute', to.fullPath);
      return next({
        name: 'LoginForm',
        query: { redirect: to.fullPath }
      });
    }

    // Verificar si la ruta requiere permisos de admin
    if (to.meta.requiresAdmin) {
      try {
        // Verificar permisos desde el backend
        const hasAdmin = await hasAdminPermission();
        if (!hasAdmin) {
          console.warn('Acceso denegado: Usuario no tiene permisos de administrador');
          return next('/mi-cuenta');
        }
      } catch (error) {
        console.error('Error verificando permisos de admin:', error);
        // Si hay error de autenticación, redirigir a login
        if (error.response?.status === 401) {
          logout();
          return next({
            name: 'LoginForm',
            query: { redirect: to.fullPath }
          });
        }
        // Si hay otro error, redirigir a cuenta
        return next('/mi-cuenta');
      }
    }
  }

  next();
});

// El interceptor para manejar errores de autenticación se ha movido al archivo apiClient.js
// para evitar dependencias circulares

export default router;